/* eslint-disable react/display-name */
import React from 'react'
import { Link } from 'react-router-dom'
import { Typography } from 'antd'
import { getDayOfYear } from 'date-fns'
import { format, zonedTimeToUtc, getTimezoneOffset } from 'date-fns-tz'
import dayjs from 'dayjs'

import { IUserLogs } from '../../types/custom-queries'
import getDateInUserTimezone from '@vacationtracker/shared/functions/get-date-in-user-timezone'
import { getQuotaInUnitsIfSet, getRawOrFormattedQuota } from '@vacationtracker/shared/functions/get-days-or-hours'

import IntlMessages from '../../util/IntlMessages'
import WorkWeek from '../work-week'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import { addTimezoneOffsetToDate } from '@vacationtracker/shared/components/utils/add-offset-to-date'
import { roundTo2Decimals } from '@vacationtracker/shared/functions/calculation-shared'
import { QuotaUnits } from '@vacationtracker/shared/types/leave-policy'
import { formatQuotaInDaysOrHours, getDaysOrHoursBasedOnUnit } from '@vacationtracker/shared/functions/quotas'
import { IFormatMessageFn, LeaveDisplayFormatEnum } from '@vacationtracker/shared/types/display-in-days-hours'
import { userHasWorkingHoursSchedule } from '@vacationtracker/shared/functions/work-week'
import { IWorkingSchedule, WorkingHours } from '@vacationtracker/shared/types/work-week'

const { Text } = Typography
interface ILogLine extends IUserLogs {
  amIAdmin: boolean
  timezone: string
  hourlyLeaveAccounting: boolean
  hourFormat: HourFormatEnum
  index: number
  workingHours?: WorkingHours | IWorkingSchedule | null
  formatMessage: IFormatMessageFn
}

interface IParams {
  startDate?: string
  endDate?: string
  quota?: number
  amount?: number
  leaveTypeName?: string
  isActive?: boolean
  yearStart?: string
  yearEnd?: string
  defaultDaysPerYear?: number
  daysPerYear?: number
  hoursPerYear?: number
  hasUnlimitedDays?: boolean
  days?: number[] | number
  changedAvatar?: boolean
  email?: string
  hours: number
  unit: QuotaUnits
  [key: string]: string | number | boolean | undefined | null | string[] | number[] | IWorkingSchedule
}

export interface IUserLogLineResult {
  key: string
  color: string
  children: string | JSX.Element
  label: string
}

const decorateDay = (dayNumber: number) => {
  const dayName = dayjs().day(dayNumber).locale('en-US').format('dddd').toLowerCase()
  return <IntlMessages id={`app.${dayName}`} ></IntlMessages>
}

const getPeriodForNonAccrual = (leaveTypeYearStart: string, leaveTypeYearEnd: string) => {
  if (getDayOfYear(new Date(leaveTypeYearStart)) === 1) {
    return format(new Date(leaveTypeYearStart), 'yyyy')
  } else {
    return `${format(new Date(leaveTypeYearStart.slice(0, 10)), 'MMM dd, yyyy')} - ${format(new Date(leaveTypeYearEnd.slice(0, 10)), 'MMM dd, yyyy')}`
  }
}

const getUserParams = (params: IParams | null) => {
  if (!params) {
    return null
  }
  let name: string | JSX.Element = ''
  let email: string | JSX.Element = ''
  let endDate: string | JSX.Element = ''
  let startDate: string | JSX.Element = ''
  let userStatus: string | JSX.Element = ''
  let isAvatarChanged = false

  if (params?.name) {
    let separator
    const paramsProperties = Object.entries(params)
    if (paramsProperties.length === 2) {
      separator = <> <IntlMessages id="app.and" /></>
    } else if (paramsProperties.length > 2) {
      separator = <>,</>
    }

    name = <>
      <IntlMessages
        id="components.logs.userUpdatedName"
        values={{
          name: params?.name,
          wrappedName: (...chunks) => <b>{chunks}</b>,
        }}
      />
      {separator}
    </>
  }

  if (params?.startDate && params?.startDate.length) {
    let separator
    if (typeof params?.isAdmin === 'boolean' && params?.endDate && params?.endDate.length) {
      separator = <>,</>
    } else if (typeof params?.isAdmin === 'boolean') {
      separator = <> <IntlMessages id="app.and" /></>
    } else if (params?.endDate && params?.endDate.length) {
      separator = <> <IntlMessages id="app.and" /></>
    }

    startDate = <>
      <IntlMessages id="components.logs.userUpdatedStartDate" values={{ startDate: format(getDateInUserTimezone(params?.startDate as string), 'MMM d, yyyy')}} />
      {separator}
    </>
  }

  if (params?.endDate && params?.endDate.length) {
    endDate = <>
      <IntlMessages id="components.logs.userUpdatedEndDate" values={{ endDate: format(getDateInUserTimezone(params?.endDate as string), 'MMM d, yyyy')}} />
      {typeof params?.isAdmin === 'boolean' && <> <IntlMessages id="app.and" /></>}
    </>
  }

  if (params?.email) {
    email = <>
      <IntlMessages
        id="components.logs.userUpdatedEmail"
        values={{
          email: <strong>{params?.email}</strong>,
        }}
      />
    </>
  }

  if (typeof params?.isAdmin === 'boolean') {
    userStatus = params?.isAdmin ?
      <IntlMessages id="components.logs.userUpdatedChangeStatusToAdmin"/> :
      <IntlMessages id="components.logs.userUpdatedChangeStatusToUser"/>
  }

  if (params?.changedAvatar) {
    isAvatarChanged = params?.changedAvatar
  }

  return {
    name: () => name,
    email: () => email,
    endDate: () => endDate,
    startDate: () => startDate,
    userStatus: () => userStatus,
    isAvatarChanged,
  }
}

const getLeaveTypeName = (leaveTypeName, isActive) => {
  if (!isActive) {
    return `${leaveTypeName} (inactive)`
  }

  return leaveTypeName
}

export const getLogLine = ({
  type,
  timestamp,
  creatorName = '<Unknown>',
  creatorId,
  paramsJson,
  amIAdmin,
  timezone,
  hourlyLeaveAccounting,
  hourFormat,
  index,
  workingHours,
  formatMessage,
}: ILogLine): IUserLogLineResult | null => {
  // Let's not convert system originated logs
  let dateInTimezone = creatorName !== 'system' ? zonedTimeToUtc(new Date(timestamp), timezone) : new Date(new Date(timestamp).toISOString().slice(0, -1))

  if (!workingHours) {
    return null
  }
  const hasSchedule = userHasWorkingHoursSchedule(workingHours)

  if (type === 'ACCRUED_DAYS') {
    // We receive accruals timestamp with time zone information, we need different logic to display the correct time in the logs.
    // Pass the timestamp date to getTimezoneOffset to get the correct offset for that specific date (handles DST correctly)
    const timestampDate = new Date(timestamp)
    dateInTimezone = addTimezoneOffsetToDate(timestampDate, getTimezoneOffset(timezone, timestampDate))
  }

  const dateTimeFormat = `MMM d, yyyy ${hourFormat === HourFormatEnum.twentyFour ? 'HH' : 'h'}:mm zzz ${hourFormat === HourFormatEnum.twentyFour ? '' : 'a'}`
  const time = format(dateInTimezone, dateTimeFormat, { timeZone: timezone })

  let text: string | JSX.Element = ''
  let color = 'blue'
  let params: IParams | null = null

  try {
    params = JSON.parse(paramsJson)
  } catch (err) {
    // Swallow the error
  }

  let creatorNameOrSystem: string | JSX.Element = <IntlMessages id="app.unknown" />

  if (creatorName !== 'system') {
    creatorNameOrSystem = creatorName
  } else if ('USER_STATUS_CHANGED' === type && params?.source === 'slack-event') {
    creatorNameOrSystem = <IntlMessages id="app.autoDeleted" />
  } else {
    creatorNameOrSystem = <IntlMessages id="app.system" />
  }

  const setLogs = () => {
    if (!params) {
      return ''
    }
    switch (type) {
      case 'USER_STATUS_CHANGED':
        if(params?.status === 'ACTIVE') {
          color = 'green'
          text = (
            <IntlMessages id="components.logs.userActivatedBy" values={{
              creatorName: creatorNameOrSystem,
              link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            }} />
          )
        }
        if (params?.status === 'INACTIVE') {
          color = 'red'
          text = (
            <IntlMessages id="components.logs.userDeactivatedBy" values={{
              creatorName: creatorNameOrSystem,
              link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            }} />
          )
        }
        if (params?.status === 'DELETED') {
          color = 'red'
          text = (
            <IntlMessages id="components.logs.userDeletedBy" values={{
              creatorName: creatorNameOrSystem,
              link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            }} />
          )
        }
        break
      case 'LOCATION_USER_MOVED':
        text = (
          <IntlMessages id="components.logs.userMovedToLocation" values={{
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            location: params?.locationName !== 'deleted' ? params?.locationName : <IntlMessages id="components.logs.deleted"/>,
            locationLink: (...chunks) => amIAdmin && params?.locationName !== 'deleted' ?
              <Link to={`/app/settings/locations/${params?.locationId}/general`}>{chunks}</Link> : <strong>{chunks}</strong>,
          }} />
        )
        break
      case 'TEAM_USER_MOVED':
        text = (
          <IntlMessages id="components.logs.userMovedToTeam" values={{
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            team: params?.teamName !== 'deleted' ? params?.teamName : <IntlMessages id="components.logs.deleted"/>,
            teamLink: (...chunks) => amIAdmin && params?.teamName !== 'deleted' ?
              <Link to={`/app/settings/departments/${params?.teamId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
          }} />
        )
        break
      case 'USER_UPDATED': {
        const userParams = getUserParams(params)
        text = (
          <>
            {userParams?.isAvatarChanged && !params?.name && <><IntlMessages id="components.logs.userChangedAvatar" /><br/></>}
            <IntlMessages id="components.logs.userUpdated" values={{
              creatorName: creatorNameOrSystem,
              link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
              ...userParams,
            }} />
          </>
        )
        break
      }
      case 'USER_INVITATION_ACCEPTED':
        text = (
          <IntlMessages id="components.logs.userInvitationAccepted" values={{
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
          }} />
        )
        break
      case 'USER_WORK_WEEK_UPDATED':
        if (params?.schedule) {
          text = (<IntlMessages id="components.logs.userWorkHoursRotatingSchedule" values={{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            totalWeeks: params?.schedule?.workHours?.length,
            startDate: (params?.schedule as IWorkingSchedule)?.startDate,
            workHours: (params?.schedule as IWorkingSchedule)?.workHours
              ? <><br/>{((params.schedule as IWorkingSchedule)?.workHours as [number, number][][]).map((week, weekIndex) => (
                <React.Fragment key={weekIndex}>
                  <IntlMessages id="components.notificationForm.week" /> {weekIndex + 1}:<br/>
                  {week.map(([day, hours]) => (
                    <React.Fragment key={`${weekIndex}-${day}`}>
                      &nbsp;&nbsp;{decorateDay(day)}: {hours} <IntlMessages id="app.hours" /><br/>
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}</>
              : undefined,
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
          }} />)
        } else
          text = (
            <IntlMessages id={params?.workHours ? 'components.logs.userWorkWeekWithHours' : 'components.logs.userWorkWeek'} values={{
              creatorName: creatorNameOrSystem,
              link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
              workDays: <WorkWeek days={params?.days as number[]} firstDayOfWeek={0} separator=", " withAnd="and " />,
              workHours: params?.workHours ? <><br/>{(params.workHours as unknown as [number, number][]).map(([day, hours]) => (
                <React.Fragment key={day}>
                  {decorateDay(day as number)}: {hours} <IntlMessages id="app.hours" /><br/>
                </React.Fragment>
              ))}</> : undefined,
            }} />
          )
        break
      case 'USER_WORK_WEEK_DELETED':
        text = (
          <IntlMessages id="components.logs.userWorkWeekDefaultLocation" values={{
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
          }} />
        )
        break
      case 'ROLLOVER_EXPIRED': {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument
        const totalDaysOrHours = getDaysOrHoursBasedOnUnit(params as any)
        text = (
          <IntlMessages
            id='components.logs.userRolloverExpired'
            values={{
              leaveTypeName: getLeaveTypeName(params?.leaveTypeName, params?.isActive),
              total: totalDaysOrHours,
              units: params.unit === 'hours' 
                ? <IntlMessages id={`app.${totalDaysOrHours > 1 ? 'hours' : 'hour'}`} />
                : <IntlMessages id={`app.${totalDaysOrHours > 1 ? 'days' : 'day'}`} />,
              b: (...chunks) => (
                <Text strong>{chunks}</Text>
              ),
            }}
          />
        )
        break
      }
      case 'YEAR_START': {
        const quota = formatQuotaInDaysOrHours(params.unit === 'hours', params[params.unit] as number, workingHours, hasSchedule)
        text = (
          <IntlMessages
            id='components.logs.userRolloverDisplay'
            values={{
              leaveTypeName: getLeaveTypeName(params?.leaveTypeName, params?.isActive),
              value: getRawOrFormattedQuota(quota, formatMessage, hasSchedule, params.unit, LeaveDisplayFormatEnum.long),
              b: (...chunks) => (
                <Text strong>{chunks}</Text>
              ),
            }} />
        )
        break
      }
      case 'ACCRUED_DAYS': {
        const quota = formatQuotaInDaysOrHours(params.unit === 'hours', params[params.unit] as number, workingHours, hasSchedule)
        if (params?.accrualType === 'NONE') {
          text = (
            <IntlMessages
              id='components.logs.nonAccrualLeaveType'
              values={{
                earned: getRawOrFormattedQuota(quota, formatMessage, hasSchedule, params.unit, LeaveDisplayFormatEnum.long),
                leaveTypeName: getLeaveTypeName(params?.leaveTypeName, params?.isActive),
                period: getPeriodForNonAccrual(params?.yearStart as string, params?.yearEnd as string),
                b: (...chunks) => (
                  <Text strong>{chunks}</Text>
                ),
              }} />
          )
        } else {
          const earned = formatQuotaInDaysOrHours(params.unit === 'hours', params.amount as number, workingHours, hasSchedule)
          text = (
            <IntlMessages
              id='components.logs.accrualDisplay'
              values={{
                // We always show the raw number of days/hours in the logs
                earned: getRawOrFormattedQuota(earned, formatMessage, true, params.unit, LeaveDisplayFormatEnum.long),
                leaveTypeName: getLeaveTypeName(params?.leaveTypeName, params?.isActive),
                defaultDaysPerYear: getRawOrFormattedQuota(quota, formatMessage, hasSchedule, params.unit, LeaveDisplayFormatEnum.long),
                b: (...chunks) => (
                  <Text strong>{chunks}</Text>
                ),
              }} />
          )
        }
        break
      }
      case 'USER_LEAVE_TYPES_UPDATED':
        text = (
          <IntlMessages id={params?.quotaSetIn === 'hours' || (hourlyLeaveAccounting && !params?.quotaSetIn) ?
            'components.logs.userLeaveTypeUpdatedQuotaHours' : 'components.logs.userLeaveTypeUpdatedQuotaDays'
          } values={{
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            leaveTypeName: params?.leaveTypeName,
            quota: () => getQuotaInUnitsIfSet(params?.daysPerYear as number, params?.hoursPerYear as number, hourlyLeaveAccounting, params?.quotaSetIn as QuotaUnits),
            b: (...chunks) => (
              <Text strong>{chunks}</Text>
            ),
          }} />
        )
        break
      case 'USER_LEAVE_TYPES_DELETED':
        text = (
          <IntlMessages id="components.logs.userLeaveTypeDeleteQuota" values={{
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            leaveTypeName: params?.leaveTypeName,
            b: (...chunks) => (
              <Text strong>{chunks}</Text>
            ),
          }} />
        )
        break
      case 'APPROVER_STATUS': {
        const teamName = params?.teamName !== 'deleted' ? params?.teamName : <IntlMessages id="components.logs.deleted"/>

        text = (
          <IntlMessages id="components.logs.userApproverStatus" values={{
            creatorName: creatorNameOrSystem,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            status: () => params?.statusChange === 'added' ? <IntlMessages id="app.set" /> : <IntlMessages id="app.removed" />,
            teamName,
            team: (...chunks) => amIAdmin && teamName !== 'deleted' ?
              <Link to={`/app/settings/departments/${params?.teamId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
          }} />
        )
        break
      }
      case 'USER_LEAVE_TYPES_ROLLOVER_UPDATED':
        text = (
          <IntlMessages id={params?.unit === 'hours' || (hourlyLeaveAccounting && !params?.unit) ?
            'components.logs.initialRolloverUpdatedHours' : 'components.logs.initialRolloverUpdatedDays'
          } values={{
            creatorName: creatorNameOrSystem,
            amount: getQuotaInUnitsIfSet(params?.days as number, params?.hours as number, hourlyLeaveAccounting, params?.unit),
            leaveTypeName: params?.leaveTypeName,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            b: (...chunks) => (
              <Text strong>{chunks}</Text>
            ),
          }} />
        )
        break
      case 'USER_LEAVE_TYPES_ROLLOVER_DELETED':
        text = (
          <IntlMessages id="components.logs.initialRolloverDeleted" values={{
            creatorName: creatorNameOrSystem,
            leaveTypeName: params?.leaveTypeName,
            link: (...chunks) => amIAdmin && creatorName !== 'system' ? <Link to={`/app/users/${creatorId}`}>{chunks}</Link> : <strong>{chunks}</strong>,
            b: (...chunks) => (
              <Text strong>{chunks}</Text>
            ),
          }} />
        )
        break
      case 'TOIL_REQUEST_APPROVED':
      case 'TOIL_REQUEST_ADDED':
        text = (
          <IntlMessages id="components.logs.earned" values={{
            total: roundTo2Decimals(params[params.unit] as number || 0),
            unit: params.unit === 'hours' ? <IntlMessages id="app.hours" /> : <IntlMessages id="app.days" />,
            leaveTypeName: params?.leaveTypeName,
            showExpiration: params?.expirationDate ? 1 : 2,
            period: params?.toilPeriod,
            expiration: params?.expirationDate,
          }} />
        )
        break
      case 'USER_SENIORITY_ENTITLEMENT':
        text = (
          <IntlMessages id="components.logs.userSeniorityEntitlement" values={{
            leaveTypeName: params?.leaveTypeName,
            totalDays: roundTo2Decimals(Number(params?.receivedProratedAmount || params?.days)),
          }} />
        )
        break
      case 'USER_SENIORITY_ENTITLEMENT_OVERRIDE':
        text = (
          <IntlMessages id="components.logs.userSeniorityEntitlementOverride" values={{
            leaveTypeName: params?.leaveTypeName,
            totalDays: roundTo2Decimals(Number(params?.days)),
          }} />
        )
        break
      case 'USER_ENTITLEMENT_BY_ROLE':
        text = (
          <IntlMessages id="components.logs.userEntitlementByRole" values={{
            leaveTypeName: params?.leaveTypeName,
            totalDays: roundTo2Decimals(Number(params?.receivedProratedAmount || params?.days)),
          }} />
        )
        break
      case 'USER_ENTITLEMENT_BY_ROLE_OVERRIDE':
        text = (
          <IntlMessages id="components.logs.userEntitlementByRoleOverride" values={{
            leaveTypeName: params?.leaveTypeName,
            totalDays: roundTo2Decimals(Number(params?.days)),
          }} />
        )
        break
      case 'USER_ENTITLEMENT_BY_ROLE_DELETED':
        text = (
          <IntlMessages id="components.logs.userEntitlementByRoleDeleted" values={{
            leaveTypeName: params?.leaveTypeName,
            labelName: params?.labelName,
            b: (...chunks) => (
              <Text strong>{chunks}</Text>
            ),
          }} />
        )
        break
      default:
        text = (type)
        break
    }
  }

  setLogs()


  return {
    key: `${time}-${index}`,
    color,
    children: text,
    label: time,
  }
}
